import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiLoader, FiAlertTriangle, FiCheck } from 'react-icons/fi'

// Painel do alerta "Saldo impossível" (auditoria 2026-08-04).
//
// Antes isso era só um texto num tooltip: dizia que a conta "Aplicação
// Financeira Sicredi" estava com -R$ 1.507.837,10 e parava aí. Diagnóstico sem
// saída vira ruído — a pessoa lê, não sabe o que fazer, e da segunda vez não lê
// mais. Aqui o número vem acompanhado da causa e do único botão que resolve.
//
// O botão é só o saldo de abertura, de propósito. Converter os 25 lançamentos
// de aplicação/resgate em transferência seria o conserto estrutural, mas exige
// escolher a conta de contrapartida de cada um — e os resgates de janeiro estão
// na Caixa Econômica, não no Sicredi, ou seja há mais de uma aplicação em jogo.
// Automatizar isso seria adivinhar. A lista fica visível para quem sabe decidir.

interface Linha {
  id: number; kind: string; description: string; payment_date: string
  amount: number; categoria: string; conta: string | null
}
interface Diag {
  conta: { id: number; name: string; type: string; initial_balance: number }
  saldo: number
  impossivel: boolean
  movimento: {
    lancamentos_proprios: number; valor_proprio: number
    transfer_in: { n: number; valor: number; de: string | null; ate: string | null }
    transfer_out: { n: number; valor: number; de: string | null; ate: string | null }
  }
  fora_da_convencao: Linha[]
  resumo: { aplicado: number; resgatado: number; saldo_economico: number }
}

const brl = (n: number) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const ptDate = (s: string | null) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '—')

export function DiagnosticoConta({ accountId, onClose, onCorrigido }: {
  accountId: number
  onClose: () => void
  onCorrigido?: () => void
}) {
  const { authenticatedFetch } = useAuth()
  const [d, setD] = useState<Diag | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [abertura, setAbertura] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [ok, setOk] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setErro(null)
    try {
      const r = await authenticatedFetch(`/api/bank-accounts/${accountId}/diagnostico`)
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Falha ao carregar')
      setD(j); setAbertura(String(j.conta.initial_balance ?? 0))
    } catch (e: any) { setErro(e.message) }
    finally { setLoading(false) }
  }, [authenticatedFetch, accountId])
  useEffect(() => { load() }, [load])

  const salvarAbertura = async () => {
    if (salvando) return
    const v = Number(String(abertura).replace(/\./g, '').replace(',', '.'))
    if (!Number.isFinite(v)) { setErro('Valor inválido'); return }
    setSalvando(true); setErro(null); setOk(false)
    try {
      const r = await authenticatedFetch(`/api/bank-accounts/${accountId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ initial_balance: v }),
      })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Falha ao salvar')
      setOk(true)
      await load()
      onCorrigido?.()
    } catch (e: any) { setErro(e.message) }
    finally { setSalvando(false) }
  }

  // Quanto faltaria de abertura para o saldo parar de ser negativo. Não é o
  // valor certo — é o PISO. O valor certo só o extrato do banco responde, e
  // dizer isso aqui evita que o mínimo vire "o número", como aconteceu com a
  // Caixinha em 03/08.
  const minimo = d && d.saldo < 0 ? Math.abs(d.saldo) + d.conta.initial_balance : null

  return (
    <SideModal
      title={d ? d.conta.name : 'Diagnóstico da conta'}
      subtitle={d ? `Saldo no sistema: ${brl(d.saldo)}` : undefined}
      icon={FiAlertTriangle}
      onClose={onClose}
      maxWidth="max-w-2xl">

      {erro && <div className="text-sm text-red-500">{erro}</div>}

      {loading || !d ? (
        <div className="py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></div>
      ) : (
        <>
          {d.impossivel && (
            <div className="neu-relief rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
              <strong>Saldo impossível.</strong> Uma conta de aplicação guarda dinheiro — não pode ficar
              negativa. Enquanto estiver assim, o Saldo Final do Fluxo de Caixa somando todas as contas
              também está errado, no mesmo valor.
            </div>
          )}

          {/* PLANOS (21/10/2026): cada parte do diagnóstico é uma seção de papel
              (.pln-secao) com título 15px; textos de apoio subiram para 13px. */}
          <section className="pln-secao">
            <h4 className="pln-secao-titulo">Como o saldo se formou</h4>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">Saldo de abertura cadastrado</td>
                  <td className="py-2 text-right tabular-nums">{brl(d.conta.initial_balance)}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">
                    Entrou por transferência
                    {d.movimento.transfer_in.n > 0 && <span className="text-[13px]"> · {d.movimento.transfer_in.n} de {ptDate(d.movimento.transfer_in.de)} a {ptDate(d.movimento.transfer_in.ate)}</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{brl(d.movimento.transfer_in.valor)}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">
                    Saiu por transferência
                    {d.movimento.transfer_out.n > 0 && <span className="text-[13px]"> · {d.movimento.transfer_out.n} de {ptDate(d.movimento.transfer_out.de)} a {ptDate(d.movimento.transfer_out.ate)}</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums text-red-600 dark:text-red-400">−{brl(d.movimento.transfer_out.valor)}</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="py-2 text-muted-foreground">Lançamentos próprios ({d.movimento.lancamentos_proprios})</td>
                  <td className="py-2 text-right tabular-nums">{brl(d.movimento.valor_proprio)}</td>
                </tr>
                <tr className="font-medium">
                  <td className="py-1.5">Saldo</td>
                  <td className={`py-1.5 text-right tabular-nums ${d.saldo < 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{brl(d.saldo)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          {d.fora_da_convencao.length > 0 && (
            <section className="pln-secao">
              <h4 className="pln-secao-titulo">Por que ficou negativo</h4>
              <p className="text-[13px] text-muted-foreground">
                Saiu {brl(d.movimento.transfer_out.valor)} de uma conta que, para o sistema, nasceu vazia.
                O dinheiro que já estava aplicado antes nunca entrou aqui: os {d.fora_da_convencao.length} lançamentos
                abaixo são aplicações e resgates registrados como despesa e receita em <em>outras</em> contas,
                sem contrapartida nesta. Somando as duas formas, a aplicação dá {brl(d.resumo.saldo_economico)}
                {d.resumo.saldo_economico < 0 && ' — ainda negativo, ou seja há aplicação anterior ao início da base que nunca foi registrada'}.
              </p>
              <div className="max-h-64 overflow-auto scrollbar-thin border border-border rounded-lg">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-background">
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="px-2.5 py-2 font-medium w-20">Data</th>
                      <th className="px-2.5 py-2 font-medium">Lançamento</th>
                      <th className="px-2.5 py-2 font-medium w-28 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.fora_da_convencao.map(l => (
                      <tr key={l.id} className="border-b border-border/40">
                        <td className="px-2.5 py-2 whitespace-nowrap text-muted-foreground">{ptDate(l.payment_date)}</td>
                        <td className="px-2 py-1.5">
                          <div className="text-foreground">{l.description}</div>
                          <div className="text-xs text-muted-foreground">{l.categoria}{l.conta ? ` · ${l.conta}` : ''}</div>
                        </td>
                        <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${l.kind === 'receita' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {l.kind === 'receita' ? '+' : '−'}{brl(l.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-muted-foreground">
                Converter esses lançamentos em transferência é o conserto definitivo, mas exige dizer
                de qual aplicação cada um saiu — e há mais de uma. Não dá para o sistema adivinhar.
              </p>
            </section>
          )}

          <section className="pln-secao">
            <h4 className="pln-secao-titulo">Corrigir: saldo de abertura</h4>
            <p className="text-[13px] text-muted-foreground">
              O valor que a aplicação tinha em <strong>01/01/2026</strong>, segundo o extrato do banco.
              É o número que falta para o saldo voltar a significar alguma coisa.
              {minimo != null && <> Abaixo de <strong>{brl(minimo)}</strong> o saldo continua negativo — mas esse é o piso aritmético, <em>não</em> o valor correto. Só o extrato responde.</>}
            </p>
            <div className="flex items-center gap-2">
              <input value={abertura} onChange={e => { setAbertura(e.target.value); setOk(false) }}
                placeholder="0,00" inputMode="decimal"
                className="h-9 flex-1 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
              <button onClick={salvarAbertura} disabled={salvando}
                className="neu-relief h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2">
                {salvando ? <FiLoader className="w-4 h-4 animate-spin" /> : ok ? <FiCheck className="w-4 h-4" /> : null}
                Salvar
              </button>
            </div>
            <p className="text-[13px] text-muted-foreground">
              A alteração fica na trilha de auditoria com o valor anterior — dá para voltar atrás.
            </p>
          </section>
        </>
      )}
    </SideModal>
  )
}
